import { Colors, ColorName } from '@kvika/audur-theme'
import { stringToInt } from '@kvika/string-utils'
import { PrismicRichText } from '@prismicio/react'

import { AudurButtonLink } from '../../components/audurButtons'
import AudurLink from '../../components/audurLink/AudurLink'
import { GridItem, MainGridContainer } from '../../components/grid-container'
import PrismicImageCard from '../../components/prismic/PrismicImageCard'
import ImageCard from '../../components/imageCard/ImageCard'
import LinkCard from '../../components/linkCard/LinkCard'
import {
	_Linkable,
	AudurContentPageEditorialContentGroup,
	AudurEditorialContent,
	AudurEditorialContentLinkGroup,
	AudurLink as AudurLinkType,
	Maybe,
	MortgageSubPageEditorialContentGroup,
} from '../../prismic/prismicTypes'
import { valueOrDefault } from '../../prismic/prismicUtils'
import {
	Alignment,
	ColumnClass,
	ContentWidth,
	EditorialContentType,
	Gutters,
	LinkSource,
	LinkStyle,
	PageLayout,
} from '../types/Types'

import styles from './renderEditorial.module.scss'

export const getAlignedColumnClass = (
	contentWidth: ContentWidth,
	alignment: Alignment,
	tabletAdoptsMobileGrid = false
) => {
	const isLeftAligned = alignment === Alignment.Left
	if (tabletAdoptsMobileGrid) {
		return ColumnClass.All
	}
	switch (contentWidth) {
		case ContentWidth._25: {
			return isLeftAligned ? ColumnClass.Left25 : ColumnClass.Right25
		}
		case ContentWidth._50: {
			return isLeftAligned ? ColumnClass.Left50 : ColumnClass.Right50
		}
		case ContentWidth._75: {
			return isLeftAligned ? ColumnClass.Left75 : ColumnClass.Right75
		}
		case ContentWidth._100: {
			return ColumnClass.All
		}
		default: {
			return ColumnClass.All
		}
	}
}

export const isLinkOfStyle = (maybeLink: Maybe<_Linkable>, linkStyle: LinkStyle) =>
	maybeLink && (maybeLink as AudurLinkType).linkStyle === linkStyle

export const getButtonLinkDomFromGroup = (linkGroup: Maybe<AudurEditorialContentLinkGroup[]>) => {
	const buttonLinkDoms = linkGroup
		?.filter(
			(g) =>
				isLinkOfStyle(g.editorialContentLink, LinkStyle.Button) ||
				isLinkOfStyle(g.editorialContentLink, LinkStyle.DarkButton)
		)
		.map((linkGroup, index) => {
			const link = linkGroup.editorialContentLink as AudurLinkType
			return (
				<AudurButtonLink
					key={`button-link-group-${index}`}
					href={valueOrDefault(link.linkUrl, '/')}
					fillColor={link.linkStyle === LinkStyle.Button ? Colors.Red : Colors.DarkGray}
					openInNewTab={false}
				>
					{valueOrDefault(link.linkTitle, '/')}
				</AudurButtonLink>
			)
		})

	return buttonLinkDoms && buttonLinkDoms.length > 0 ? (
		<div className={styles.buttonLinkContainer}>{buttonLinkDoms}</div>
	) : null
}

export const getLinkDomFromGroup = (linkGroup: Maybe<AudurEditorialContentLinkGroup[]>) => {
	const linkDoms = linkGroup
		?.filter((g) => isLinkOfStyle(g.editorialContentLink, LinkStyle.Regular))
		.map((linkGroup, index) => {
			const link = linkGroup.editorialContentLink as AudurLinkType
			return (
				<AudurLink key={`link-group-${index}`} href={valueOrDefault(link.linkUrl, '/')}>
					{valueOrDefault(link.linkTitle, '/')}
				</AudurLink>
			)
		})

	return linkDoms && linkDoms.length > 0 ? <div className={styles.linkContainer}>{linkDoms}</div> : null
}

export const renderEditorialContent = (
	editorialContent: AudurEditorialContent,
	pageLayout: PageLayout,
	isMobile: boolean,
	isTabletOrMobile: boolean
) => {
	const textContentDom = (
		<div className={[styles.textContainer, styles.header].join(' ')}>
			{editorialContent.title && (
				<div className={styles.titleWrapper}>
					<PrismicRichText field={editorialContent.title} />
				</div>
			)}
			{editorialContent.description && (
				<div className={styles.descriptionWrapper}>
					<PrismicRichText
						field={editorialContent.description}
						components={{
							preformatted: (children) => {
								if (children.text) {
									return <span dangerouslySetInnerHTML={{ __html: children.text }} />
								}
								return null
							},
						}}
					/>
				</div>
			)}
		</div>
	)

	const imageDom = editorialContent.image ? (
		<div className={styles.imageContainer}>
			<PrismicImageCard editorialContent={editorialContent} />
		</div>
	) : null

	const linkContainerDom = getLinkDomFromGroup(editorialContent?.linkGroup)
	const buttonLinkContainerDom = getButtonLinkDomFromGroup(editorialContent?.linkGroup)
	const contentGridWidth = valueOrDefault(editorialContent.contentWidth, ContentWidth._50) as ContentWidth
	const imageGridWidth = `${100 - stringToInt(contentGridWidth)}` as ContentWidth
	const isHeader = (contentType: EditorialContentType) => contentType === EditorialContentType.Header
	const hasTabletMobileGrid = pageLayout === PageLayout.AppLayout && isTabletOrMobile

	// Content alignment states where rich text content and links should be placed
	// in the page, the image will not follow this alignment and will be
	// placed exactly opposite the content alignment
	switch (editorialContent.contentAlignment) {
		case Alignment.Left: {
			if ((isMobile || hasTabletMobileGrid) && !isHeader(editorialContent.contentType as EditorialContentType)) {
				// On mobile, the left alignment needs to be inverted so the image is rendered
				// above the content - unless its a header, then the image renders below
				return (
					<>
						{imageDom && (
							<GridItem className={`${getAlignedColumnClass(imageGridWidth, Alignment.Left, hasTabletMobileGrid)}`}>
								{imageDom}
							</GridItem>
						)}
						<GridItem
							className={`${getAlignedColumnClass(contentGridWidth, Alignment.Right, hasTabletMobileGrid)} ${
								styles.gridContent
							}`}
						>
							{textContentDom}
							{linkContainerDom}
							{buttonLinkContainerDom}
						</GridItem>
					</>
				)
			}
			return (
				<>
					<GridItem
						className={`${getAlignedColumnClass(contentGridWidth, Alignment.Left, hasTabletMobileGrid)} ${
							styles.gridContent
						}`}
					>
						{textContentDom}
						{linkContainerDom}
						{buttonLinkContainerDom}
					</GridItem>
					{imageDom && (
						<GridItem className={`${getAlignedColumnClass(imageGridWidth, Alignment.Right, hasTabletMobileGrid)}`}>
							{imageDom}
						</GridItem>
					)}
				</>
			)
		}
		case Alignment.Right: {
			return (
				<>
					{imageDom && (
						<GridItem className={`${getAlignedColumnClass(imageGridWidth, Alignment.Left, hasTabletMobileGrid)}`}>
							{imageDom}
						</GridItem>
					)}
					<GridItem
						className={`${getAlignedColumnClass(contentGridWidth, Alignment.Right, hasTabletMobileGrid)} ${
							styles.gridContent
						}`}
					>
						{textContentDom}
						{linkContainerDom}
						{buttonLinkContainerDom}
					</GridItem>
				</>
			)
		}
		default: {
			return null
		}
	}
}

export const renderWelcomeContent = (
	editorialContent: AudurEditorialContent,
	pageLayout: PageLayout,
	_isMobile: boolean,
	isTabletOrMobile: boolean
) => {
	// Create heading JSX element matching AudurWelcomeContent structure exactly
	const heading = editorialContent.title ? (
		<div className={ColorName.DarkGray}>
			<h1>
				<PrismicRichText
					field={editorialContent.title}
					components={{
						paragraph: ({ children }) => <span>{children}</span>,
					}}
				/>
			</h1>
		</div>
	) : undefined

	// Extract plain text from description field to match original structure
	const getPlainTextFromRichText = (richTextField: unknown) => {
		if (!richTextField || !Array.isArray(richTextField)) return ''
		return richTextField.map((block: { text?: string }) => block.text || '').join(' ')
	}

	// Create subHeading JSX element matching AudurWelcomeContent structure exactly
	const subHeadingText = getPlainTextFromRichText(editorialContent.description)
	const subHeading = subHeadingText ? (
		<div className={ColorName.DarkGray}>
			<h4>{subHeadingText}</h4>
		</div>
	) : undefined

	// Extract links from linkGroup and convert to LinkSource format
	const links = editorialContent.linkGroup || []
	const primaryLink = links[0]?.editorialContentLink as AudurLinkType
	const secondaryLink = links[1]?.editorialContentLink as AudurLinkType

	const linkSource: LinkSource = {
		href: valueOrDefault(primaryLink?.linkUrl, '/'),
		title: valueOrDefault(primaryLink?.linkTitle, 'Nánar'),
		fillColor: Colors.DarkGray,
	}

	const secondLinkSource: LinkSource | undefined = secondaryLink
		? {
				href: valueOrDefault(secondaryLink.linkUrl, '/'),
				title: valueOrDefault(secondaryLink.linkTitle, 'Sækja appið'),
			}
		: undefined

	// Image component matching AudurWelcomeContent
	const imageDom = editorialContent.image ? (
		<ImageCard
			imageSource={{
				src: editorialContent.image.url || '/svg/flower_v2.svg',
				alt: editorialContent.image.alt || 'flower',
				width: editorialContent.imageWidth || 800,
				height: editorialContent.imageHeight || 1080,
			}}
		/>
	) : (
		<ImageCard
			imageSource={{
				src: '/svg/flower_v2.svg',
				alt: 'flower',
				width: 800,
				height: 1080,
			}}
		/>
	)

	const hasTabletMobileGrid = pageLayout === PageLayout.AppLayout && isTabletOrMobile

	// Match AudurWelcomeContent structure exactly
	return (
		<>
			<GridItem
				className={getAlignedColumnClass(ContentWidth._50, Alignment.Left, hasTabletMobileGrid)}
				style={{ justifyContent: 'center' }}
			>
				{imageDom}
			</GridItem>
			<GridItem
				className={`${getAlignedColumnClass(ContentWidth._50, Alignment.Right, hasTabletMobileGrid)} ${
					styles.gridContent
				}`}
				style={{ justifyContent: 'center' }}
			>
				<LinkCard
					style={{ paddingBottom: 64 }}
					className={styles.welcomeHeading}
					heading={heading}
					subHeading={subHeading}
					linkSource={linkSource}
					secondLinkSource={secondLinkSource}
				/>
			</GridItem>
		</>
	)
}

export const renderEditorialItemGroup = (
	group: AudurContentPageEditorialContentGroup | MortgageSubPageEditorialContentGroup,
	pageLayout: PageLayout,
	index: number,
	isMobile: boolean,
	isTabletOrMobile: boolean
) => {
	const editorial = group.contentPageEditorial as AudurEditorialContent
	const pageLayoutStyle =
		pageLayout === PageLayout.Standard ? styles.standardContentContainer : styles.appLayoutContentContainer

	switch (editorial?.contentType) {
		case EditorialContentType.Header: {
			return (
				<MainGridContainer
					key={`editorial-header-${index}`}
					backgroundColor={Colors.DarkGray}
					gutters={editorial.image ? Gutters.LeftOnly : Gutters.Both}
					className={`${pageLayoutStyle} ${styles.headerContainer}`}
				>
					{renderEditorialContent(editorial, pageLayout, isMobile, isTabletOrMobile)}
				</MainGridContainer>
			)
		}
		case EditorialContentType.Inline: {
			return (
				<MainGridContainer
					key={`editorial-inline-item-${index}`}
					backgroundColor={Colors.Silver}
					gutters={Gutters.Both}
					className={`${pageLayoutStyle} ${styles.inlineContentContainer}`}
				>
					{renderEditorialContent(editorial, pageLayout, isMobile, isTabletOrMobile)}
				</MainGridContainer>
			)
		}
		case EditorialContentType.PageSection: {
			return (
				<MainGridContainer
					key={`editorial-page-section-${index}`}
					backgroundColor={Colors.LightOrange}
					gutters={Gutters.Both}
					className={`${pageLayoutStyle} ${styles.pageSectionContainer}`}
				>
					{renderEditorialContent(editorial, pageLayout, isMobile, isTabletOrMobile)}
				</MainGridContainer>
			)
		}
		case EditorialContentType.WelcomeContent: {
			return (
				<MainGridContainer
					key={`editorial-welcome-content-${index}`}
					backgroundColor={Colors.LightOrange}
					gutters={Gutters.Both}
					className={`${pageLayoutStyle} ${styles.welcomeContentContainer}`}
				>
					{renderWelcomeContent(editorial, pageLayout, isMobile, isTabletOrMobile)}
				</MainGridContainer>
			)
		}
		default: {
			return null
		}
	}
}
